import React from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { createVideoMetadata } from '@jwp/ott-common/src/utils/metadata';
import PlayTrailer from '@jwp/ott-theme/assets/icons/play_trailer.svg?react';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { formatAgeRating } from '@jwp/ott-common/src/utils/formatting';
import useMovieData from '@jwp/ott-hooks-react/src/useMovieData';
import useMovieParams from '@jwp/ott-ui-react/src/hooks/useMovieParams';
import useMovieHandlers from '@jwp/ott-ui-react/src/hooks/useMovieHandlers';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import Button from '@jwp/ott-ui-react/src/components/Button/Button';
import Icon from '@jwp/ott-ui-react/src/components/Icon/Icon';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import VideoDefinitionList from '@jwp/ott-ui-react/src/components/VideoDefintionsList/VideoDefinitionList';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import MediaHeroInfo from '@jwp/ott-ui-react/src/components/MediaHeroInfo/MediaHeroInfo';
import MediaHeroButtons from '@jwp/ott-ui-react/src/components/MediaHeroButtons/MediaHeroButtons';
import MediaHero from '@jwp/ott-ui-react/src/components/MediaHero/MediaHero';
import MediaTabs from '@jwp/ott-ui-react/src/components/MediaTabs/MediaTabs';
import MarkdownComponent from '@jwp/ott-ui-react/src/components/MarkdownComponent/MarkdownComponent';
import MediaTabAbout from '@jwp/ott-ui-react/src/components/MediaTabAbout/MediaTabAbout';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';
import TruncatedText from '@jwp/ott-ui-react/src/components/TruncatedText/TruncatedText';
import Link from '@jwp/ott-ui-react/src/components/Link/Link';
import VideoTags from '@jwp/ott-ui-react/src/components/VideoTags/VideoTags';
import styles from '@jwp/ott-ui-react/src/pages/ScreenRouting/mediaScreens/MediaMovie/MediaMovie.module.scss';
import env from '@jwp/ott-common/src/env';

const MediaReading: ScreenComponent<PlaylistItem> = ({ data, isLoading }) => {
  const { t } = useTranslation('video');
  const breakpoint = useBreakpoint();
  const { id, play, feedId } = useMovieParams();
  const {
    accessModel,
    isLoggedIn,
    hasSubscription,
    playlist,
    playlistTitle,
    nextItem,
    playUrl,
    movieURL,
    trailerItem,
    hasTrailer,
    isFavoritesEnabled,
    isEntitled,
    playTrailer,
    isPlaylistLoading,
    setPlayTrailer,
    getCardUrl,
  } = useMovieData(data, id, feedId);
  const { handleBack, handleComplete } = useMovieHandlers(nextItem, movieURL, playlist);
  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;
  const smallScreen = breakpoint < Breakpoint.md;
  const trailerButton = hasTrailer ? (
    <Button
      label={t('video:trailer')}
      aria-label={t('video:watch_trailer')}
      startIcon={<Icon icon={PlayTrailer} />}
      onClick={() => setPlayTrailer(true)}
      active={playTrailer}
      disabled={!trailerItem}
    />
  ) : undefined;

  const customMetadata = [
    ...createVideoMetadata(
      { ...data, pubdate: 0, rating: '' },
      {
        hoursAbbreviation: t('common:abbreviation.hours'),
        minutesAbbreviation: t('common:abbreviation.minutes'),
      },
    ),
    ...(data?.reader ? [data.reader as string] : []),
  ];

  const readerTags = [data?.audioLanguages, formatAgeRating(data?.rating)].filter(Boolean);

  const purchaseLink =
    data?.purchase_link_url && data?.purchase_link_display ? (
      <Link href={data.purchase_link_url as string} target="_blank">
        {data.purchase_link_display as string}
      </Link>
    ) : (
      ''
    );

  useAutoFocus(id);

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} />
      <MediaHero image={data.backgroundImage}>
        <MediaHeroInfo
          title={data.title}
          description={data.synopsis ? (data.synopsis as string) : <TruncatedText text={data.description} maximumLines={2} className={styles.description} />}
          metadataComponent={<VideoMetaData attributes={customMetadata} />}
          tagsComponent={<VideoTags attributes={readerTags} />}
          definitionComponent={
            <VideoDefinitionList
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.author'), value: data?.author as string },
                { label: t('video:metadata.illustrator'), value: data?.illustrator as string },
                { label: t('video:metadata.publisher'), value: data?.publisher as string },
              ]}
            />
          }
        />
        <MediaHeroButtons
          cta={
            <>
              <StartWatchingButton
                key={id} // necessary to fix autofocus on TalkBack
                item={data}
                playUrl={playUrl}
              />
              {!smallScreen && trailerButton}
            </>
          }
        >
          {smallScreen && trailerButton}
          {isFavoritesEnabled ? <FavoriteButton item={data} /> : undefined}
          <ShareButton title={data.title} description={data.description} url={canonicalUrl} />
        </MediaHeroButtons>
      </MediaHero>
      <MediaTabs titles={[playlistTitle, t('video:about_tab')]} isLoading={isLoading || isPlaylistLoading}>
        {playlist && (
          <CardGrid
            playlist={playlist}
            isLoading={isLoading || isPlaylistLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            currentCardItem={data}
            currentCardLabel={t('video:current_video')}
            hasSubscription={hasSubscription}
            getUrl={getCardUrl}
          />
        )}
        <MediaTabAbout description={<MarkdownComponent className={styles.markdown} markdownString={data.description} headerOffset={1} />}>
          <div className={styles.aboutDefinitionList}>
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.age'), value: formatAgeRating(data?.rating as string) },
                { label: t('video:metadata.audio_languages'), value: data?.audioLanguages as string },
                { label: t('video:metadata.reader'), value: data?.reader as string },
              ]}
            />
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.author'), value: data?.author as string },
                { label: t('video:metadata.illustrator'), value: data?.illustrator as string },
                { label: t('video:metadata.publisher'), value: data?.publisher as string },
                { label: t('video:metadata.purchase_link'), value: purchaseLink, hiddenLabel: true },
              ]}
            />
          </div>
        </MediaTabAbout>
      </MediaTabs>
      <Cinema
        open={play && isEntitled}
        onClose={handleBack}
        item={data}
        title={data.title}
        primaryMetadata={
          <>
            <VideoTags attributes={readerTags} />
            <VideoMetaData attributes={customMetadata} />
          </>
        }
        onComplete={handleComplete}
        feedId={feedId ?? undefined}
        onNext={handleComplete}
      />
      <TrailerModal item={trailerItem} title={`${data.title} - Trailer`} open={playTrailer} onClose={() => setPlayTrailer(false)} />
    </React.Fragment>
  );
};

export default MediaReading;
